import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Chip } from "@/components/sync/EndpointCard";
import { utc } from "@/components/sync/syncText";

const td = "px-2 py-1 border-t border-[var(--border-subtle)] align-top";
const sel = "h-7 text-xs bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded";
const CAT = { discrepancy: "text-rose-300", duplicate: "text-rose-300", incompatible: "text-amber-300", stale: "text-amber-300", unavailable: "text-slate-300", excluded: "text-slate-400", match: "text-emerald-300" };

export function CountCards({ s }) {
  return (
    <div data-testid="lc-counts" className="grid grid-cols-2 md:grid-cols-7 gap-2">
      {s.categories.map((c) => (
        <div key={c} data-testid={`lc-count-${c}`} className="rounded-md border border-[var(--border-subtle)] p-3">
          <div className="text-[10px] uppercase tracking-wide text-slate-500">{c}</div>
          <div className={`text-lg font-semibold ${CAT[c]}`}>{s.counts[c]}</div>
        </div>
      ))}
    </div>
  );
}

export function ComparisonList({ s }) {
  return (
    <ul data-testid="lc-comparisons" className="space-y-1 text-[11px] text-slate-400">
      {s.comparisons.map((c) => (
        <li key={c.key} data-testid={`lc-cmp-${c.key}`} className="flex flex-wrap gap-2">
          <span className={`font-semibold ${c.computed ? "text-emerald-300" : "text-slate-500"}`}>{c.computed ? "COMPARED" : "EXCLUDED"}</span>
          <span className="text-slate-200">{c.label}</span><span className="text-slate-500">— {c.basis}</span>
        </li>
      ))}
    </ul>
  );
}

export function SeparationLine({ s }) {
  const p = s.separation;
  return (
    <div data-testid="lc-separation" className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-400">
      <span>Snapshot: {s.snapshot ? `${s.snapshot.run_id} · ${utc(s.snapshot.captured_at)}` : "none"} <Chip status={s.snapshot_freshness} testId="lc-freshness" /></span>
      <span>Compared (real): {p.real_holdings} holdings · {p.real_ledger_entries} ledger entries</span>
      <span data-testid="lc-excluded-sides">Not compared: {p.demo_holdings} demo + {p.unlabelled_holdings} unlabelled holdings · {p.demo_ledger_entries} demo + {p.unlabelled_ledger_entries} unlabelled ledger entries · {p.paper_positions} paper positions</span>
      <span data-testid="lc-funds-excluded">Funds fields excluded: {s.funds_fields_excluded}</span>
      {p.real_ledger_truncated && <span data-testid="lc-truncated" className="text-amber-300">Ledger results truncated: only the first {p.real_ledger_entries} real entries were compared</span>}
    </div>
  );
}

const val = (o) => (o ? o.quantity ?? "—" : "—");

export function RowsTable({ grant }) {
  const [f, setF] = useState({ q: "", category: "", scope: "", sort: "category", desc: false, page: 1 });
  const [qIn, setQIn] = useState("");
  const set = (k, v) => setF((p) => ({ ...p, [k]: v, page: k === "page" ? v : 1 }));
  useEffect(() => {
    const t = setTimeout(() => set("q", qIn), 350);
    return () => clearTimeout(t);
  }, [qIn]);
  const { data: d, isError } = useQuery({
    queryKey: ["lc-rows", grant, f], gcTime: 0,
    queryFn: async () => (await api.get("/ledger-compare/rows", { headers: { "X-Portfolio-Grant": grant }, params: { ...f, category: f.category || undefined, scope: f.scope || undefined, page_size: 25 } })).data,
  });
  if (isError) return <div data-testid="lc-rows-error" className="text-[11px] text-rose-300">Details are locked or expired. Unlock again.</div>;
  const pages = d ? Math.max(1, Math.ceil(d.total / d.page_size)) : 1;
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        <Input data-testid="lc-search" value={qIn} onChange={(e) => setQIn(e.target.value)} placeholder="Search symbol or reason" className="h-7 w-48 text-xs bg-[var(--bg-canvas)] border-[var(--border-subtle)]" />
        <select data-testid="lc-category" value={f.category} onChange={(e) => set("category", e.target.value)} className={sel}><option value="">All categories</option>{Object.keys(CAT).map((c) => <option key={c} value={c}>{c}</option>)}</select>
        <select data-testid="lc-scope" value={f.scope} onChange={(e) => set("scope", e.target.value)} className={sel}><option value="">All scopes</option>{["holdings", "ledger", "funds", "positions", "comparison"].map((c) => <option key={c} value={c}>{c}</option>)}</select>
        <select data-testid="lc-sort" value={f.sort} onChange={(e) => set("sort", e.target.value)} className={sel}>{["category", "reason", "symbol", "scope"].map((c) => <option key={c} value={c}>{`Sort: ${c}`}</option>)}</select>
        <button data-testid="lc-order" onClick={() => set("desc", !f.desc)} className="text-xs text-slate-300 hover:text-white">{f.desc ? "Descending" : "Ascending"}</button>
      </div>
      <div className="overflow-x-auto">
        <table data-testid="lc-rows" className="w-full text-[11px] text-slate-400">
          <thead><tr className="text-left text-slate-500">{["Category", "Reason", "Scope", "Symbol", "Broker reported", "Tradify recorded", "Evidence"].map((h) => <th key={h} className="px-2 py-1 font-medium">{h}</th>)}</tr></thead>
          <tbody>
            {d?.rows.map((r) => (
              <tr key={r.id} data-testid={`lc-row-${r.id}`}>
                <td className={`${td} font-semibold ${CAT[r.category]}`}>{r.category}</td><td className={`${td} text-slate-200`}>{r.reason}</td>
                <td className={td}>{r.scope}</td><td className={td}>{r.symbol || "—"}</td>
                <td className={td}>{val(r.broker)}</td><td className={td}>{val(r.tradify)}</td>
                <td className={`${td} font-mono text-[10px]`}>{r.evidence.broker && <div>broker: {r.evidence.broker.run_id}</div>}{r.evidence.tradify && <div>{r.evidence.tradify.collection}#{r.evidence.tradify.ref}</div>}{r.evidence.fields_excluded != null && <div>{r.evidence.fields_excluded} fields</div>}{r.evidence.comparison && <div>{r.evidence.comparison}</div>}</td>
              </tr>
            ))}
            {d && !d.rows.length && <tr><td colSpan={7} data-testid="lc-rows-empty" className={`${td} text-slate-500`}>No rows match.</td></tr>}
          </tbody>
        </table>
      </div>
      {d && (
        <div className="flex items-center justify-between text-[11px] text-slate-400">
          <span data-testid="lc-total">{d.total} row(s){d.truncated ? " · ledger truncated" : ""}</span>
          <div className="flex gap-2"><button data-testid="lc-prev" disabled={f.page <= 1} onClick={() => set("page", f.page - 1)} className="disabled:opacity-40">Prev</button><span>{f.page} / {pages}</span><button data-testid="lc-next" disabled={f.page >= pages} onClick={() => set("page", f.page + 1)} className="disabled:opacity-40">Next</button></div>
        </div>
      )}
    </div>
  );
}
